/**
 * The daily e-mail digest preference (spec 2026-10-27-23): the
 * `email_digest` user setting. Opt-in: an absent setting is off, and turning
 * it on keeps 07:00 on working days unless the member changes them.
 */
export const EMAIL_DIGEST_SETTING = 'email_digest'
export const TIME_ZONE_SETTING = 'time_zone'

export interface DigestPreference {
    enabled: boolean
    hour: number
    /** ISO weekdays, Monday = 1 … Sunday = 7. */
    days: number[]
}

export const DEFAULT_DIGEST: DigestPreference = { enabled: false, hour: 7, days: [1, 2, 3, 4, 5] }

/** A stored value read over the defaults: an unwritten key keeps its default. */
export function readDigest(value: Partial<DigestPreference> | undefined): DigestPreference {
    return {
        enabled: value?.enabled ?? DEFAULT_DIGEST.enabled,
        hour: typeof value?.hour === 'number' ? value.hour : DEFAULT_DIGEST.hour,
        days: Array.isArray(value?.days) ? [...value.days].sort((a, b) => a - b) : [...DEFAULT_DIGEST.days],
    }
}

/** Toggles one weekday in a preference's days, kept sorted. */
export function toggleDay(days: number[], day: number): number[] {
    return (days.includes(day) ? days.filter((d) => d !== day) : [...days, day]).sort((a, b) => a - b)
}

/** The browser's IANA time zone, or undefined when it cannot tell. */
export function browserTimeZone(): string | undefined {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
    } catch {
        return undefined
    }
}

/** Localized weekday names, Monday first, for ISO days 1 to 7. */
export function weekdayNames(locale: string): { day: number; label: string }[] {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'long' })
    // 2026-11-02 is a Monday.
    return Array.from({ length: 7 }, (_, i) => ({ day: i + 1, label: fmt.format(new Date(Date.UTC(2026, 10, 2 + i, 12))) }))
}
